import {
  useCallback,
  useEffect,
  useId,
  useMemo,
  useRef,
  useState,
  type KeyboardEvent,
} from "react";
import { Check, ChevronDown, Plus } from "lucide-react";
import { comparableText as comparable, normalizeComboboxValue } from "./text";

/**
 * Combobox criável — o controle de Origem, Segmento e Função do contato.
 *
 * Estes campos NÃO são listas fechadas (AGENTS §14.4): o usuário digita o que
 * quiser, o controle recomenda o que já existe e nada limita a resposta ao
 * catálogo. A dívida que este componente fecha (§25.7) é a lista de sugestões
 * nativa do navegador, que não tem semântica de combobox, não é navegável por
 * teclado de forma previsível e não segue o visual do produto.
 *
 * Duas decisões deliberadas:
 *
 * - **Criar é ação explícita.** Digitar e sair do campo não inventa catálogo;
 *   confirmar exige escolher a opção "Usar «texto»" ou apertar Enter sobre ela,
 *   que é o mesmo que §7.1 exige dos catálogos.
 * - **O valor é o texto normalizado**, não um id. Origem e segmento são texto
 *   livre no domínio; transformar em enum fechado é justamente o que §14.4
 *   proíbe.
 */
export interface ComboboxProps {
  /** Valores já existentes na base, para recomendação. */
  suggestions: readonly string[];
  value: string;
  onChange: (value: string) => void;
  label?: string;
  labelledBy?: string;
  placeholder?: string;
  size?: "default" | "field";
  disabled?: boolean;
  disabledReason?: string;
  invalid?: boolean;
  describedBy?: string;
  className?: string;
  id?: string;
  /** Texto do item que confirma um valor novo. */
  createLabel?: (text: string) => string;
}

export function Combobox({
  suggestions,
  value,
  onChange,
  label,
  labelledBy,
  placeholder = "Digite ou escolha",
  size = "field",
  disabled = false,
  disabledReason,
  invalid,
  describedBy,
  className,
  id,
  createLabel = (text) => `Usar “${text}”`,
}: ComboboxProps) {
  const generatedId = useId();
  const baseId = id ?? generatedId;
  const listId = `${baseId}-list`;

  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState(value);
  const [activeIndex, setActiveIndex] = useState(-1);

  const rootRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLInputElement>(null);
  const listRef = useRef<HTMLUListElement>(null);
  // Fechar devolve o foco ao campo. Sem esta trava, o próprio `focus` reabriria
  // a lista e escolher uma sugestão nunca terminaria.
  const skipNextFocusOpen = useRef(false);

  // O campo mostra o valor confirmado enquanto a lista está fechada; enquanto
  // aberta, mostra o que está sendo digitado.
  useEffect(() => {
    if (!open) setQuery(value);
  }, [open, value]);

  const catalog = useMemo(() => {
    const seen = new Set<string>();
    const unique: string[] = [];
    for (const item of suggestions) {
      const text = normalizeComboboxValue(item);
      if (!text) continue;
      const key = comparable(text);
      if (seen.has(key)) continue;
      seen.add(key);
      unique.push(text);
    }
    return unique.sort((left, right) => left.localeCompare(right, "pt-BR"));
  }, [suggestions]);

  const typed = normalizeComboboxValue(query);
  const filtered = useMemo(() => {
    if (!typed) return catalog;
    const needle = comparable(typed);
    return catalog.filter((item) => comparable(item).includes(needle));
  }, [catalog, typed]);

  // "Usar «texto»" só aparece quando o texto ainda não existe no catálogo:
  // oferecer criar o que já existe convidaria a duplicar com outra grafia.
  const canCreate = typed.length > 0 && !catalog.some((item) => comparable(item) === comparable(typed));
  const items = useMemo(
    () => (canCreate ? [...filtered, { create: typed }] : filtered),
    [canCreate, filtered, typed],
  );

  const openList = useCallback(() => {
    if (disabled) return;
    setOpen(true);
    setActiveIndex(-1);
  }, [disabled]);

  const openOnFocus = useCallback(() => {
    if (skipNextFocusOpen.current) {
      skipNextFocusOpen.current = false;
      return;
    }
    openList();
  }, [openList]);

  const closeList = useCallback((restoreFocus = true) => {
    setOpen(false);
    setActiveIndex(-1);
    // Se o foco já está no campo (teclado), não há o que restaurar; devolver o
    // foco aí dispararia o `focus` que reabre a lista. Quando o clique tirou o
    // foco, a trava consome exatamente o evento de volta.
    if (restoreFocus && document.activeElement !== inputRef.current) {
      skipNextFocusOpen.current = true;
      inputRef.current?.focus();
    }
  }, []);

  const commit = useCallback(
    (index: number) => {
      const item = items[index];
      if (item === undefined) return;
      const next = typeof item === "string" ? item : normalizeComboboxValue(item.create);
      onChange(next);
      setQuery(next);
      closeList();
    },
    [closeList, items, onChange],
  );

  useEffect(() => {
    if (!open) return;
    const onPointerDown = (event: MouseEvent) => {
      if (rootRef.current?.contains(event.target as Node)) return;
      // Clicar fora abandona o que foi digitado e não confirmado: criar é ação
      // explícita, nunca efeito colateral de sair do campo.
      setOpen(false);
      setActiveIndex(-1);
      setQuery(value);
    };
    document.addEventListener("mousedown", onPointerDown);
    return () => document.removeEventListener("mousedown", onPointerDown);
  }, [open, value]);

  useEffect(() => {
    if (!open || activeIndex < 0) return;
    const node = listRef.current?.querySelector<HTMLElement>(
      `#${CSS.escape(`${baseId}-opt-${activeIndex}`)}`,
    );
    node?.scrollIntoView?.({ block: "nearest" });
  }, [activeIndex, baseId, open]);

  const move = (step: number) => {
    if (items.length === 0) return;
    setActiveIndex((current) => {
      const from = current < 0 ? (step > 0 ? -1 : 0) : current;
      return (from + step + items.length) % items.length;
    });
  };

  const handleKeyDown = (event: KeyboardEvent<HTMLInputElement>) => {
    if (disabled) return;
    switch (event.key) {
      case "ArrowDown":
        event.preventDefault();
        if (!open) openList();
        else move(1);
        break;
      case "ArrowUp":
        event.preventDefault();
        if (!open) openList();
        else move(-1);
        break;
      case "Home":
        if (open) {
          event.preventDefault();
          setActiveIndex(0);
        }
        break;
      case "End":
        if (open) {
          event.preventDefault();
          setActiveIndex(items.length - 1);
        }
        break;
      case "Enter":
        if (!open) return;
        event.preventDefault();
        // Enter sem item ativo confirma o que está escrito, que é o que o
        // usuário acabou de declarar; ele continua sendo uma ação explícita.
        if (activeIndex >= 0) commit(activeIndex);
        else if (typed) {
          onChange(typed);
          setQuery(typed);
          closeList();
        } else closeList();
        break;
      case "Escape":
        if (!open) return;
        event.preventDefault();
        event.stopPropagation();
        setQuery(value);
        closeList();
        break;
      case "Tab":
        if (!open) return;
        if (activeIndex >= 0) commit(activeIndex);
        setOpen(false);
        setActiveIndex(-1);
        break;
      default:
        break;
    }
  };

  const describedByValue =
    [describedBy, disabled && disabledReason ? `${baseId}-reason` : null]
      .filter(Boolean)
      .join(" ") || undefined;

  return (
    <div ref={rootRef} className={className ? `cx-select ${className}` : "cx-select"}>
      <div className="cx-combobox">
        <input
          ref={inputRef}
          id={baseId}
          type="text"
          role="combobox"
          className="cx-input"
          data-size={size === "field" ? "field" : undefined}
          autoComplete="off"
          aria-autocomplete="list"
          aria-expanded={open}
          aria-controls={open ? listId : undefined}
          aria-activedescendant={
            open && activeIndex >= 0 ? `${baseId}-opt-${activeIndex}` : undefined
          }
          aria-label={labelledBy ? undefined : label}
          aria-labelledby={labelledBy}
          aria-invalid={invalid || undefined}
          aria-describedby={describedByValue}
          placeholder={placeholder}
          disabled={disabled}
          title={disabled ? disabledReason : undefined}
          value={query}
          onChange={(event) => {
            setQuery(event.target.value);
            setActiveIndex(-1);
            if (!open) setOpen(true);
          }}
          onFocus={openOnFocus}
          onKeyDown={handleKeyDown}
        />
        <button
          type="button"
          className="cx-combobox-toggle"
          tabIndex={-1}
          aria-label={open ? "Fechar sugestões" : "Mostrar sugestões"}
          disabled={disabled}
          onClick={() => {
            if (open) {
              closeList();
              return;
            }
            skipNextFocusOpen.current = false;
            inputRef.current?.focus();
            openList();
          }}
        >
          <ChevronDown size={14} aria-hidden="true" />
        </button>
      </div>

      {disabled && disabledReason && (
        <span id={`${baseId}-reason`} className="cx-sr-only">
          {disabledReason}
        </span>
      )}

      {open && items.length > 0 && (
        <ul
          ref={listRef}
          id={listId}
          role="listbox"
          className="cx-select-menu"
          aria-label={labelledBy ? undefined : label}
          aria-labelledby={labelledBy}
          tabIndex={-1}
        >
          {items.map((item, index) => {
            const isCreate = typeof item !== "string";
            const text = isCreate ? item.create : item;
            return (
              <li
                key={isCreate ? `__create__${text}` : text}
                id={`${baseId}-opt-${index}`}
                role="option"
                className="cx-select-option"
                aria-selected={!isCreate && comparable(text) === comparable(value)}
                data-active={index === activeIndex ? "true" : undefined}
                onMouseEnter={() => setActiveIndex(index)}
                onClick={() => commit(index)}
              >
                {isCreate ? (
                  <>
                    <Plus size={14} aria-hidden="true" />
                    <span>{createLabel(text)}</span>
                  </>
                ) : (
                  <>
                    <span>{text}</span>
                    {comparable(text) === comparable(value) && (
                      <Check className="cx-select-check" size={14} aria-hidden="true" />
                    )}
                  </>
                )}
              </li>
            );
          })}
        </ul>
      )}
    </div>
  );
}
